import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useReveal from '@/hooks/useReveal';
import { servicesData } from '@/mocks/services';

const categories = ['All', 'Development', 'AI', 'Marketing', 'Creative', 'Infrastructure'];

const categoryMap: Record<string, string[]> = {
  'Development': ['website-development'],
  'AI': ['ai-automation'],
  'Marketing': ['seo-marketing', 'meta-ads', 'social-analytics'],
  'Creative': ['video-editing'],
  'Infrastructure': ['hosting'],
};

const benefits = [
  { icon: 'ri-timer-flash-line', title: 'Faster Delivery', desc: 'AI-assisted workflows cut development time by 40% without compromising quality.' },
  { icon: 'ri-bar-chart-grouped-line', title: 'Data-Driven', desc: 'Every decision is backed by analytics, A/B testing, and performance metrics.' },
  { icon: 'ri-shield-check-line', title: 'Enterprise Security', desc: 'Bank-grade security, SSL, GDPR compliance, and regular audits built in.' },
  { icon: 'ri-customer-service-2-line', title: 'Dedicated Support', desc: 'Your project manager is available 24/7. No tickets, no queues — direct access.' },
  { icon: 'ri-loop-left-line', title: 'Continuous Iteration', desc: 'We monitor, measure, and optimize even after launch. Growth never stops.' },
  { icon: 'ri-stack-line', title: 'Full Stack Under One Roof', desc: 'From design to deployment to marketing — one team, one vision, zero handoff gaps.' },
];

const processSteps = [
  { step: '01', title: 'Discovery', desc: 'We dive deep into your business, goals, audience, and competitive landscape.' },
  { step: '02', title: 'Strategy', desc: 'Roadmap, tech stack, architecture, and content strategy — all documented.' },
  { step: '03', title: 'Design', desc: 'Wireframes to high-fidelity prototypes. Interactive, responsive, and on-brand.' },
  { step: '04', title: 'Build', desc: 'Clean, scalable code with AI integration, performance optimization, and testing.' },
  { step: '05', title: 'Launch', desc: 'Deployment, monitoring, SEO setup, and analytics tracking from day one.' },
  { step: '06', title: 'Grow', desc: 'Continuous optimization, A/B testing, content updates, and feature expansion.' },
];

export default function ServicesPage() {
  useReveal();
  const [activeCat, setActiveCat] = useState('All');

  const filtered = activeCat === 'All'
    ? servicesData
    : servicesData.filter((s) => categoryMap[activeCat]?.includes(s.slug));

  return (
    <div className="bg-ink-950 text-white min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-[clamp(105px,8.5vw,135px)] pb-12 sm:pb-16 overflow-hidden w-full">
        <img
          src="https://readdy.ai/api/search-image?query=3D%20rendered%20abstract%20holographic%20technology%20scene%20with%20floating%20translucent%20geometric%20shapes%20and%20glowing%20purple%20neon%20digital%20streams%20in%20deep%20dark%20space%2C%20soft%20violet%20and%20cyan%20haze%2C%20glossy%20isometric%20render%20style%2C%20floating%20holographic%20interface%20elements%2C%20premium%20cinematic%20atmosphere&width=1800&height=1200&seq=fc-sv2-hero&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/80 via-ink-950/70 to-ink-950" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 md:px-8 w-full">
          <div className="max-w-3xl">
            <div className="text-xs font-mono tracking-widest text-neon-400">// SERVICES</div>
            <h1 className="mt-4 font-display text-4xl sm:text-6xl md:text-7xl font-bold leading-[1.08] tracking-tight text-gradient">
              Your Digital<br />Growth Stack
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
              End-to-end technology services — from building your website to automating your operations and driving growth.
            </p>
          </div>
        </div>
      </section>

      {/* CATEGORY FILTERS */}
      <section className="relative py-6 sm:py-8 px-4 sm:px-6 border-b border-white/5">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-wrap gap-2.5 sm:gap-3 justify-center">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-medium transition whitespace-nowrap ${
                  activeCat === c
                    ? 'bg-neon-500/20 text-neon-300 border border-neon-500/40'
                    : 'glass text-white/60 border border-white/5 hover:border-white/20 hover:text-white'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ALL SERVICES */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-12 sm:gap-16">
            {filtered.map((s, i) => (
              <div key={s.slug} className={`reveal grid md:grid-cols-2 gap-8 md:gap-12 items-center ${i % 2 === 1 ? '' : ''}`}>
                <div className={`${i % 2 === 1 ? 'md:order-2' : ''}`}>
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-neon-600 to-neon-400 text-white text-2xl mb-4">
                    <i className={s.icon} />
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">{s.title}</h2>
                  <p className="mt-3 text-xs sm:text-sm text-white/70 leading-relaxed">{s.fullDesc}</p>

                  <div className="mt-5">
                    <h4 className="text-xs font-semibold text-neon-300 mb-2.5">What we deliver:</h4>
                    <ul className="space-y-1.5">
                      {s.offerings.slice(0, 5).map((o) => (
                        <li key={o} className="flex items-start gap-2 text-xs sm:text-sm text-white/60">
                          <i className="ri-check-line text-neon-400 mt-0.5 shrink-0" /> {o}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {s.tech.slice(0, 6).map((t) => (
                      <span key={t} className="px-2.5 py-1 rounded-full text-[11px] font-mono bg-white/5 border border-white/10 text-white/70">{t}</span>
                    ))}
                  </div>

                  <Link
                    to={`/services/${s.slug}`}
                    className="mt-6 inline-flex items-center gap-2 btn-primary text-white font-semibold px-6 py-3 rounded-lg text-xs sm:text-sm whitespace-nowrap"
                  >
                    Explore {s.title} <i className="ri-arrow-right-line" />
                  </Link>
                </div>
                <div className={`${i % 2 === 1 ? 'md:order-1' : ''} relative group`}>
                  <div className="absolute -inset-4 rounded-3xl bg-neon-500/20 blur-3xl opacity-60 group-hover:opacity-100 transition duration-500" />
                  <img
                    src={s.image}
                    alt={s.title}
                    className="relative rounded-2xl w-full max-h-80 md:max-h-full object-cover border border-white/10 group-hover:border-neon-500/30 transition duration-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="relative mx-auto max-w-7xl">
          <div className="text-center reveal">
            <div className="text-xs font-mono tracking-widest text-neon-400">// HOW WE WORK</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-bold text-gradient">Our Process</h2>
            <p className="mt-3 text-xs sm:text-sm text-white/60 max-w-xl mx-auto">A battle-tested workflow refined across 120+ projects. Transparent, iterative, and built for results.</p>
          </div>
          <div className="mt-10 sm:mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {processSteps.map((p, i) => (
              <div key={p.step} className="reveal group glass rounded-xl p-6 sm:p-7 hover:border-neon-500/30 transition relative overflow-hidden" style={{ transitionDelay: `${i * 50}ms` }}>
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition">
                  <span className="font-display text-4xl sm:text-5xl font-bold text-neon-400">{p.step}</span>
                </div>
                <div className="font-mono text-xs text-neon-400 font-bold mb-2">Step {p.step}</div>
                <h3 className="font-display font-semibold text-base sm:text-lg text-white">{p.title}</h3>
                <p className="mt-2 text-xs sm:text-sm text-white/60 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <div className="text-center reveal">
            <div className="text-xs font-mono tracking-widest text-neon-400">// WHY US</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-bold text-gradient">The FormaCode Advantage</h2>
          </div>
          <div className="mt-10 sm:mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <div key={b.title} className="reveal glass rounded-xl p-6 sm:p-7 hover:border-neon-500/30 transition group" style={{ transitionDelay: `${i * 50}ms` }}>
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-neon-600/50 to-neon-400/50 text-neon-300 text-lg group-hover:from-neon-600 group-hover:to-neon-400 group-hover:text-white transition">
                  <i className={b.icon} />
                </div>
                <h3 className="mt-4 font-display font-semibold text-base sm:text-lg text-white">{b.title}</h3>
                <p className="mt-2 text-xs sm:text-sm text-white/60 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="relative mx-auto max-w-4xl text-center reveal">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-gradient">Need Multiple Services?</h2>
          <p className="mt-4 text-white/70 text-base sm:text-lg">We create custom bundles that combine web, AI, marketing, and hosting into one cohesive system.</p>
          <Link to="/contact" className="btn-primary inline-flex mt-8 text-white font-semibold px-8 py-3.5 rounded-lg text-sm whitespace-nowrap items-center gap-2">
            Get a Custom Quote <i className="ri-rocket-2-line" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}